import {
    ChangeDetectorRef,
    Component,
    inject,
    OnInit,
    viewChild,
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { EcmrStatus } from '@app/shared/models/ecmr/EcmrStatus';
import { ActivatedRoute, Router } from '@angular/router';
import { Signature } from '@app/shared/models/ecmr/areas/signature/Signature';
import { SignatureType } from '@app/shared/models/ecmr/SignatureType';
import { PayerType } from '@app/core/enums/ecmr/PayerType';
import { MatDialog } from '@angular/material/dialog';
import { DateAdapter } from '@angular/material/core';
import { LoadFromTemplateDialogComponent } from '@app/shared/components/ecmr/dialogs/load-from-template-dialog/load-from-template-dialog.component';
import { Ecmr } from '@app/shared/models/ecmr/Ecmr';
import { TemplateUser } from '@app/shared/models/ecmr/TemplateUser';
import { TemplateNameDialogComponent } from '@app/shared/components/ecmr/dialogs/template-name-dialog/template-name-dialog.component';
import {
    forkJoin,
    lastValueFrom,
    Observable,
    startWith,
    Subscription,
    switchMap,
} from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { EcmrConsignment } from '@app/shared/models/ecmr/EcmrConsignment';
import { isEcmrDcaCompliant } from '@app/shared/components/ecmr/ecmr-dca.util';
import { EcmrEditorService } from '@app/core/services/ecmr-services/ecmr-editor.service';
import { EcmrRole } from '@app/core/enums/ecmr/EcmrRole';
import { CountryCode } from '@app/core/enums/ecmr/CountryCode';
import { EcmrService } from '@app/core/services/ecmr-services/ecmr.service';
import { EcmrTransportType } from '@app/shared/models/ecmr/EcmrTransportType';
import { DateTimeService } from '@app/core/services/ecmr-services/date-time.service';
import { SignaturePadDialogComponent } from '@app/shared/components/ecmr/dialogs/signature-pad-dialog/signature-pad-dialog.component';
import { ExternalUserService } from '@app/core/services/ecmr-services/external-user.service';
import { SealMetadata } from '@app/shared/models/ecmr/SealMetadata';
import {
    ConfirmationDialogResult,
    EcmrConfirmationDialogComponent,
} from '@app/shared/components/ecmr/dialogs/ecmr-confirmation-dialog/ecmr-confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { MatAccordion } from '@angular/material/expansion';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { ToastService } from '@app/core/services/toast.service';
import { EcmrActionService } from '@app/core/services/ecmr-services/ecmr-action.service';
import { UserRoles } from '../../../../../api/models/user-roles';
import { SaveWarningDialogComponent } from '@app/shared/components/ecmr/dialogs/save-warning-dialog/save-warning-dialog.component';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/shared/material/material.module';
import { TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DynamicDisableControlDirective } from '@app/shared/components/ecmr/dynamic-disable-control.directive';
import { EcmrDocumentsComponent } from '@app/shared/components/ecmr/ecmr-editor/ecmr-documents/ecmr-documents.component';
import { LatestMotorPlatePipe } from '@app/shared/pipes/latest-motor-plate.pipe';
import { vehicleConfigCompletenessValidator } from '@app/shared/form-validators';

export enum EditorMode {
    ECMR_EDIT = 'ECMR_EDIT',
    ECMR_COPY = 'ECMR_COPY',
    ECMR_NEW = 'ECMR_NEW',
    TEMPLATE_EDIT = 'TEMPLATE_EDIT',
    TEMPLATE_NEW = 'TEMPLATE_NEW',
}

type VehicleConfigurationFormGroup = FormGroup<{
    timestamp: FormControl<Date | null>;
    motorVehicleLicensePlate: FormControl<string | null>;
    trailerLicensePlate: FormControl<string | null>;
    secondTrailerLicensePlate: FormControl<string | null>;
}>;

type SpecialPermitFormGroup = FormGroup<{
    type: FormControl<string | null>;
    reference: FormControl<string | null>;
    description: FormControl<string | null>;
}>;

export function phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const phoneRegex = /^(\+)?[0-9]*$/;
        const valid = phoneRegex.test(control.value);
        return valid ? null : { invalidPhoneNumber: true };
    };
}

export function emailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const valid = emailRegex.test(control.value);
        return valid ? null : { invalidEmail: true };
    };
}

export function pastDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        // The control may hold a Luxon DateTime, a moment, or a native Date;
        // toJSDate()/toDate() are used by the respective adapters, else fall back.
        const value = control.value;
        const date: Date =
            typeof value?.toJSDate === 'function'
                ? value.toJSDate()
                : typeof value?.toDate === 'function'
                  ? value.toDate()
                  : new Date(value);

        return date.getTime() >= Date.now() ? { futureDate: true } : null;
    };
}

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-ecmr-editor',
    templateUrl: './ecmr-editor.component.html',
    styleUrl: './ecmr-editor.component.scss',
    imports: [
        CommonModule,
        MaterialModule,
        TranslateModule,
        ReactiveFormsModule,
        DynamicDisableControlDirective,
        EcmrDocumentsComponent,
        LatestMotorPlatePipe,
    ],
})
export class EcmrEditorComponent implements OnInit {
    private dateAdapter = inject<DateAdapter<unknown>>(DateAdapter);

    isReadOnly: boolean = false;

    canFillSenderFields: boolean = false;
    canFillCarrierFields: boolean = false;
    canFillConsigneeFields: boolean = false;
    canCarrierEditInTransport: boolean = false;

    // Upper bound for vehicle-configuration date pickers (timestamp must be in
    // the past). Uses the active DateAdapter's type (Luxon DateTime here), not a
    // native Date, so the picker's [max] comparison works.
    today = this.dateAdapter.today();

    isExternalUser: boolean;
    id: string;
    tan: string;
    userToken: string;

    countries = Object.keys(CountryCode);
    filteredConsigneeCountries: Observable<string[]>;
    filteredSenderCountries: Observable<string[]>;
    filteredCarrierCountries: Observable<string[]>;
    filteredSuccessiveCarrierCountries: Observable<string[]>;

    accordion = viewChild(MatAccordion);

    ecmrToEdit: Ecmr;
    loadedTemplate: TemplateUser;
    ecmrConsignment: EcmrConsignment;

    userEcmrRoles: EcmrRole[] = [];

    payerType: PayerType[] = Object.values(PayerType);

    sealMetadata: SealMetadata[] = [];
    consigneeSignature: Signature | null;

    alignSenderAndConsigneeFields: boolean = false;

    itemList = new FormArray([
        new FormGroup({
            marksAndNos: new FormGroup({
                logisticsShippingMarksMarking: new FormControl<string | null>(
                    null
                ),
                logisticsShippingMarksCustomBarcodeList: new FormArray<
                    FormControl<string | null>
                >([
                    new FormControl<string | null>(null, [
                        Validators.minLength(2),
                        Validators.maxLength(35),
                    ]),
                ]),
            }),
            numberOfPackages: new FormGroup({
                logisticsPackageItemQuantity: new FormControl<number | null>(
                    null
                ),
            }),
            methodOfPacking: new FormGroup({
                logisticsPackageType: new FormControl<string | null>(null),
            }),
            natureOfTheGoods: new FormGroup({
                transportCargoIdentification: new FormControl<string | null>(
                    null
                ),
            }),
            grossWeightInKg: new FormGroup({
                supplyChainConsignmentItemGrossWeight: new FormControl<
                    number | null
                >(null),
            }),
            volumeInM3: new FormGroup({
                supplyChainConsignmentItemGrossVolume: new FormControl<
                    number | null
                >(null),
            }),
        }),
    ]);

    ecmrConsignmentFormGroup = new FormGroup({
        //Area 1
        senderInformation: new FormGroup({
            senderCompanyName: new FormControl<string | null>(null),
            senderPersonName: new FormControl<string | null>(null),
            senderStreet: new FormControl<string | null>(null),
            senderPostcode: new FormControl<string | null>(null),
            senderCity: new FormControl<string | null>(null),
            senderCountryCode: new FormGroup({
                value: new FormControl<string | null>(null),
                region: new FormControl<string | null>(null),
            }),
            taxNumber: new FormControl<string | null>(null, [
                Validators.maxLength(255),
            ]),
            senderContactInformation: new FormGroup({
                email: new FormControl<string | null>(null, [emailValidator()]),
                phone: new FormControl<string | null>(null, [
                    phoneNumberValidator(),
                ]),
            }),
        }),
        //Area 2
        multiConsigneeShipment: new FormGroup({
            isMultiConsigneeShipment: new FormControl<boolean | null>(false), //qq
        }),
        consigneeInformation: new FormGroup({
            consigneeCompanyName: new FormControl<string | null>(null),
            consigneePersonName: new FormControl<string | null>(null),
            consigneeStreet: new FormControl<string | null>(null),
            consigneePostcode: new FormControl<string | null>(null),
            consigneeCity: new FormControl<string | null>(null),
            consigneeCountryCode: new FormGroup({
                value: new FormControl<string | null>(null),
                region: new FormControl<string | null>(null),
            }),
            consigneeContactInformation: new FormGroup({
                email: new FormControl<string | null>(null, [emailValidator()]),
                phone: new FormControl<string | null>(null, [
                    phoneNumberValidator(),
                ]),
            }),
        }),
        //Area 3
        takingOverTheGoods: new FormGroup({
            takingOverTheGoodsPlace: new FormControl<string | null>(null),
            logisticsTimeOfArrivalDateTime: new FormControl<Date | null>(null),
            logisticsTimeOfDepartureDateTime: new FormControl<Date | null>(
                null
            ),
        }),
        //Area 4
        deliveryOfTheGoods: new FormGroup({
            logisticsLocationOpeningHours: new FormControl<string | null>(null),
            logisticsLocationCity: new FormControl<string | null>(null),
        }),
        //Area 5
        sendersInstructions: new FormGroup({
            transportInstructionsDescription: new FormControl<string | null>(
                null
            ),
        }),
        //Area 6
        carrierInformation: new FormGroup({
            carrierCompanyName: new FormControl<string | null>(null),
            carrierDriverName: new FormControl<string | null>(null),
            carrierStreet: new FormControl<string | null>(null),
            carrierPostcode: new FormControl<string | null>(null),
            carrierCity: new FormControl<string | null>(null),
            carrierCountryCode: new FormGroup({
                value: new FormControl<string | null>(null),
                region: new FormControl<string | null>(null),
            }),
            taxNumber: new FormControl<string | null>(null, [
                Validators.maxLength(255),
            ]),
            vehicleConfigurationHistory:
                new FormArray<VehicleConfigurationFormGroup>([]),
            carrierContactInformation: new FormGroup({
                email: new FormControl<string | null>(null, [emailValidator()]),
                carrierPhone: new FormControl<string | null>(null, [
                    phoneNumberValidator(),
                ]),
                driverPhone: new FormControl<string | null>(null, [
                    phoneNumberValidator(),
                ]),
            }),
        }),
        //Area 7
        successiveCarrierInformation: new FormGroup({
            successiveCarrierCity: new FormControl<string | null>(null),
            successiveCarrierCountryCode: new FormGroup({
                value: new FormControl<string | null>(null),
                region: new FormControl<string | null>(null),
            }),
            successiveCarrierCompanyName: new FormControl<string | null>(null),
            successiveCarrierDriverName: new FormControl<string | null>(null),
            successiveCarrierPostcode: new FormControl<string | null>(null),
            successiveCarrierStreet: new FormControl<string | null>(null),
            taxNumber: new FormControl<string | null>(null, [
                Validators.maxLength(255),
            ]),
            vehicleConfigurationHistory:
                new FormArray<VehicleConfigurationFormGroup>([]),
            successiveCarrierContactInformation: new FormGroup({
                email: new FormControl<string | null>(null, [emailValidator()]),
                carrierPhone: new FormControl<string | null>(null, [
                    phoneNumberValidator(),
                ]),
                driverPhone: new FormControl<string | null>(null, [
                    phoneNumberValidator(),
                ]),
            }),
        }),
        //Area 8
        carriersReservationsAndObservationsOnTakingOverTheGoods: new FormGroup({
            carrierReservationsObservations: new FormControl<string | null>(
                null
            ),
            senderReservationsObservationsSignature:
                new FormControl<Signature | null>(null),
        }),
        //Area 9
        documentsHandedToCarrier: new FormGroup({
            documentsRemarks: new FormControl<string | null>(null),
        }),
        itemList: this.itemList,
        specialPermits: new FormArray<SpecialPermitFormGroup>([]),
        //Area 16
        specialAgreementsSenderCarrier: new FormGroup({
            customSpecialAgreement: new FormControl<string | null>(null),
        }),
        //Area 17
        toBePaidBy: new FormGroup({
            customChargeCarriage: new FormGroup({
                value: new FormControl<number | null>(null),
                currency: new FormControl<string | null>(null),
                payer: new FormControl<PayerType | null>(null),
            }),
            customChargeSupplementary: new FormGroup({
                value: new FormControl<number | null>(null),
                currency: new FormControl<string | null>(null),
                payer: new FormControl<PayerType | null>(null),
            }),
            customChargeCustomsDuties: new FormGroup({
                value: new FormControl<number | null>(null),
                currency: new FormControl<string | null>(null),
                payer: new FormControl<PayerType | null>(null),
            }),
            customChargeOther: new FormGroup({
                value: new FormControl<number | null>(null),
                currency: new FormControl<string | null>(null),
                payer: new FormControl<PayerType | null>(null),
            }),
        }),
        //Area 18
        otherUsefulParticulars: new FormGroup({
            customParticulars: new FormControl<string | null>(null),
        }),
        //Area 19
        cashOnDelivery: new FormGroup({
            customCashOnDelivery: new FormControl<number | null>(null),
        }),
        //Area 21
        established: new FormGroup({
            customEstablishedIn: new FormControl<string | null>(null),
            customEstablishedDate: new FormControl<Date | null>(null),
        }),
        //Area 24
        goodsReceived: new FormGroup({
            consigneeSignatureDate: new FormControl<Date | null>(null),
            confirmedLogisticsLocationName: new FormControl<string | null>(
                null
            ),
            consigneeReservationsObservations: new FormControl<string | null>(
                null
            ),
            consigneeTimeOfArrival: new FormControl<Date | null>(null),
            consigneeTimeOfDeparture: new FormControl<Date | null>(null),
            consigneeSignature: new FormControl<Signature | null>(null),
        }),
        //Area 25
        nonContractualPartReservedForTheCarrier: new FormGroup({
            nonContractualCarrierRemarks: new FormControl<string | null>(null),
        }),
        //Area 26
        referenceIdentificationNumber: new FormGroup({
            value: new FormControl<string>('', Validators.required),
        }),
    });

    sub: Subscription;

    // Clears the cross-field Spanish (DeCA) tax-number requirement once no actor
    // sits in Spain anymore. @UntilDestroy auto-unsubscribes this property.
    private taxRequirementSub = new Subscription();

    // Clears the conditional successive-carrier requirement once the section is
    // empty again. @UntilDestroy auto-unsubscribes this property.
    private successiveCarrierRequirementSub: Subscription;

    editorMode: EditorMode;
    protected readonly EditorMode = EditorMode;

    protected dateTimeService = inject(DateTimeService);

    constructor(
        private router: Router,
        private sessionService: SessionService,
        private matDialog: MatDialog,
        private ecmrEditorService: EcmrEditorService,
        private ecmrService: EcmrService,
        protected ecmrActionService: EcmrActionService,
        private externalUserService: ExternalUserService,
        private cd: ChangeDetectorRef,
        private toastr: ToastService,
        private translateService: TranslateService,
        private route: ActivatedRoute,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.id = this.route.snapshot.params['id'];
        this.tan = this.route.snapshot.params['tan'];
        this.userToken = this.route.snapshot.params['userToken'];
        this.isExternalUser = !!this.tan && !!this.userToken;
        if (this.id) {
            this.editorMode = EditorMode.ECMR_EDIT;
        } else {
            this.editorMode = EditorMode.ECMR_NEW;
        }

        if (this.route.snapshot.url.join('/').includes('template-editor')) {
            this.editorMode = EditorMode.TEMPLATE_NEW;
            this.sub = this.route.params.subscribe(params => {
                this.id = params['id'];
                if (this.id) {
                    this.editorMode = EditorMode.TEMPLATE_EDIT;
                }
            });
        }

        if (this.router.url.includes('copy')) {
            this.editorMode = EditorMode.ECMR_COPY;
        }

        this.initializeForm();
        // Initialize Country Autocomplete Form Fields
        const formGroupControl = this.ecmrConsignmentFormGroup.controls;
        //Autocomplete filter Sender Countries
        this.filteredSenderCountries =
            formGroupControl.senderInformation.controls.senderCountryCode.controls.value.valueChanges.pipe(
                startWith(
                    formGroupControl.senderInformation.controls
                        .senderCountryCode.controls.value.value ?? ''
                ),
                map(value => this._filter(value ?? ''))
            );

        //Autocomplete filter Consignee Countries
        this.filteredConsigneeCountries =
            formGroupControl.consigneeInformation.controls.consigneeCountryCode.controls.value.valueChanges.pipe(
                startWith(
                    formGroupControl.consigneeInformation.controls
                        .consigneeCountryCode.controls.value.value ?? ''
                ),
                map(value => this._filter(value ?? ''))
            );

        //Autocomplete filter Carrier Countries
        this.filteredCarrierCountries =
            this.ecmrConsignmentFormGroup.controls.carrierInformation.controls.carrierCountryCode.controls.value.valueChanges.pipe(
                startWith(
                    formGroupControl.carrierInformation.controls
                        .carrierCountryCode.controls.value.value ?? ''
                ),
                map(value => this._filter(value ?? ''))
            );

        this.filteredSuccessiveCarrierCountries =
            this.ecmrConsignmentFormGroup.controls.successiveCarrierInformation.controls.successiveCarrierCountryCode.controls.value.valueChanges.pipe(
                startWith(
                    formGroupControl.successiveCarrierInformation.controls
                        .successiveCarrierCountryCode.controls.value.value ?? ''
                ),
                map(value => this._filter(value ?? ''))
            );

        // When an actor's country changes away from Spain, drop the DeCA
        // tax-number requirement the moment no actor is in Spain anymore, so the
        // stale "tax number required" error clears without a further sign click.
        const countryControls = [
            formGroupControl.senderInformation.controls.senderCountryCode
                .controls.value,
            formGroupControl.carrierInformation.controls.carrierCountryCode
                .controls.value,
            formGroupControl.consigneeInformation.controls.consigneeCountryCode
                .controls.value,
            formGroupControl.successiveCarrierInformation.controls
                .successiveCarrierCountryCode.controls.value,
        ];
        countryControls.forEach(ctrl =>
            this.taxRequirementSub.add(
                ctrl.valueChanges.subscribe(() => {
                    if (!this.hasSpanishActor()) {
                        this.clearTaxMandatoryForSigning();
                    }
                })
            )
        );

        // Once every successive-carrier field is emptied again, drop its
        // mandatory-for-signing errors without waiting for a further sign click.
        this.successiveCarrierRequirementSub =
            formGroupControl.successiveCarrierInformation.valueChanges.subscribe(
                () => {
                    if (!this.hasSuccessiveCarrierInput()) {
                        this.clearSuccessiveCarrierMandatoryForSigning();
                    }
                }
            );

        const site = this.sessionService.currentOrganizationSiteSignal();
        if (site) {
            this.isReadOnly =
                site.roles.includes(UserRoles.RemoteConsignor) ||
                site.roles.includes(UserRoles.RemoteCarrier) ||
                site.roles.includes(UserRoles.RemoteConsignee);
        }
    }

    returnToOverview(showWarning: boolean) {
        const formDirty =
            this.ecmrConsignmentFormGroup.dirty ||
            this.ecmrConsignmentFormGroup.controls.itemList.dirty;
        if (showWarning && formDirty) {
            // check if changes saved
            const dialogRef = this.dialog.open(SaveWarningDialogComponent, {
                width: '40vw',
                data: {},
            });

            dialogRef.afterClosed().subscribe(result => {
                if (result === 'closeWithoutSaving') {
                    this.navigateToOverview();
                } else if (result === 'saveAndExit') {
                    if (this.isTemplate()) {
                        this.saveTemplate(true);
                    } else {
                        this.saveEcmr(true);
                    }
                }
            });
        } else {
            this.navigateToOverview();
        }
    }

    private navigateToOverview(): void {
        // Determine base route from current URL
        const currentUrl = this.router.url;
        let baseRoute: string;

        if (currentUrl.includes('/consignor/')) {
            baseRoute = RoutingEndpoints.consignor;
        } else if (
            currentUrl.includes('/spediteur/') ||
            currentUrl.includes('/carrier/')
        ) {
            baseRoute = RoutingEndpoints.carrier;
        } else if (currentUrl.includes('/consignee/')) {
            baseRoute = RoutingEndpoints.consignee;
        } else {
            // Fallback to history.back() if route can't be determined
            window.history.back();
            return;
        }

        // Navigate to the overview page
        // The active tab (overview/templates/archive) is preserved in appStore.currentEcmrSubView
        // and will be automatically restored by the view component
        this.router.navigate([baseRoute]);
    }

    private initializeForm() {
        if (
            this.editorMode === EditorMode.TEMPLATE_NEW ||
            this.editorMode === EditorMode.TEMPLATE_EDIT
        ) {
            this.ecmrConsignmentFormGroup.controls.itemList.controls = [];
            this.ecmrConsignmentFormGroup.controls['itemList'].disable();
            this.ecmrConsignmentFormGroup.controls['goodsReceived'].disable();
            this.ecmrConsignmentFormGroup.controls[
                'carriersReservationsAndObservationsOnTakingOverTheGoods'
            ].disable();
        }
        if (
            this.editorMode === EditorMode.ECMR_NEW ||
            this.editorMode === EditorMode.TEMPLATE_NEW
        ) {
            this.ecmrConsignment =
                this.ecmrEditorService.createEmptyEcmrConsignment();
            this.ensureCarrierVehicleConfig();
            this.userEcmrRoles = [EcmrRole.Sender];
            this.setFormConstraints();
        }
        if (this.editorMode === EditorMode.ECMR_EDIT) {
            if (this.tan != undefined && this.userToken != undefined) {
                const loadEcmrObs = this.externalUserService.getEcmrWithTan(
                    this.id,
                    this.userToken,
                    this.tan
                );
                const loadRolesObs =
                    this.externalUserService.getEcmrRolesForUser(
                        this.id,
                        this.userToken,
                        this.tan
                    );
                forkJoin({ ecmr: loadEcmrObs, roles: loadRolesObs }).subscribe(
                    result => {
                        this.loadEcmr(result.ecmr);
                        this.userEcmrRoles = result.roles;
                        this.setFormConstraints();
                    }
                );
            } else {
                const loadEcmrObs = this.ecmrEditorService.getEcmr(this.id);
                const loadRolesObs =
                    this.ecmrService.getEcmrRolesForCurrentUser(
                        this.id,
                        this.sessionService.currentOrganizationSiteKey()
                    );
                forkJoin({ ecmr: loadEcmrObs, roles: loadRolesObs }).subscribe(
                    result => {
                        this.loadEcmr(result.ecmr);
                        this.userEcmrRoles = result.roles;
                        this.setFormConstraints();
                        this.scrollIfSigning();
                    }
                );
            }
        }
        if (this.editorMode === EditorMode.ECMR_COPY) {
            this.ecmrEditorService.getEcmr(this.id).subscribe(ecmr => {
                this.userEcmrRoles = [EcmrRole.Sender];
                this.loadEcmr(this.ecmrEditorService.copyEcmr(ecmr));
                this.setFormConstraints();
            });
        }
        if (this.editorMode === EditorMode.TEMPLATE_EDIT) {
            this.ecmrEditorService
                .getTemplate(
                    this.sessionService.currentOrganizationSiteKey(),
                    Number.parseFloat(this.id)
                )
                .subscribe(ecmr => {
                    this.userEcmrRoles = [EcmrRole.Sender];
                    this.canFillSenderFields = true;
                    this.loadedTemplate = ecmr;
                    this.loadEcmr(ecmr.ecmr);
                    this.setFormConstraints();
                });
        }
    }

    resetForm() {
        this.ecmrConsignmentFormGroup.reset();

        this.ecmrConsignmentFormGroup.controls.itemList.controls = [];
        this.addNewItem();
    }

    scrollIfSigning() {
        if (this.route.snapshot.queryParams['action'] === 'sign') {
            const element = document.getElementById('ecmr-signature-scroll');
            element?.scrollIntoView();
        }
    }

    addNewItem() {
        this.ecmrConsignmentFormGroup.controls.itemList.controls.push(
            new FormGroup({
                marksAndNos: new FormGroup({
                    logisticsShippingMarksMarking: new FormControl(),
                    logisticsShippingMarksCustomBarcodeList: new FormArray<
                        FormControl<string | null>
                    >([this.createBarcodeControl()]),
                }),
                numberOfPackages: new FormGroup({
                    logisticsPackageItemQuantity: new FormControl(),
                }),
                methodOfPacking: new FormGroup({
                    logisticsPackageType: new FormControl(),
                }),
                natureOfTheGoods: new FormGroup({
                    transportCargoIdentification: new FormControl(),
                }),
                grossWeightInKg: new FormGroup({
                    supplyChainConsignmentItemGrossWeight: new FormControl(),
                }),
                volumeInM3: new FormGroup({
                    supplyChainConsignmentItemGrossVolume: new FormControl(),
                }),
            })
        );
    }

    loadFromTemplate() {
        // TODO: implement template loading
        this.matDialog
            .open(LoadFromTemplateDialogComponent, {
                width: '90%',
            })
            .afterClosed()
            .subscribe(dialogResult => {
                if (dialogResult) {
                    this.loadedTemplate = dialogResult;
                    this.ecmrConsignment = dialogResult.ecmr.ecmrConsignment;
                    this.ecmrConsignmentFormGroup.controls.itemList.controls =
                        [];
                    this.addNewItem();
                    this.ecmrConsignmentFormGroup.patchValue(
                        this.ecmrConsignment as any
                    );
                    this.loadBarcodesIntoForm();
                    this.loadListsIntoForm();
                }
            });
    }

    checkEcmrStatusNew() {
        return (
            this.ecmrToEdit?.ecmrStatus &&
            this.ecmrToEdit.ecmrStatus !== EcmrStatus.NEW
        );
    }

    saveTemplate(returnToOverview: boolean) {
        if (
            this.ecmrConsignmentFormGroup.valid &&
            this.editorMode !== EditorMode.TEMPLATE_EDIT
        ) {
            this.matDialog
                .open(TemplateNameDialogComponent, {
                    minWidth: '350px',
                })
                .afterClosed()
                .pipe(
                    filter(dialogResult => dialogResult),
                    switchMap(dialogResult => {
                        const formValues: EcmrConsignment =
                            this.transformItemsForApi(
                                this.ecmrConsignmentFormGroup.getRawValue()
                            );
                        const ecmr: Ecmr = {
                            ecmrId: null,
                            ecmrConsignment: formValues,
                        };
                        return this.ecmrEditorService.saveTemplate(
                            this.sessionService.currentOrganizationSiteKey(),
                            ecmr,
                            dialogResult
                        );
                    })
                )
                .subscribe(() => {
                    if (returnToOverview) this.returnToOverview(false);
                });
        }

        if (
            this.ecmrConsignmentFormGroup.valid &&
            this.editorMode == EditorMode.TEMPLATE_EDIT
        ) {
            this.loadedTemplate.ecmr.ecmrConsignment =
                this.transformItemsForApi(
                    this.ecmrConsignmentFormGroup.getRawValue()
                );
            this.ecmrEditorService
                .updateTemplate(
                    this.sessionService.currentOrganizationSiteKey(),
                    this.loadedTemplate
                )
                .subscribe(() => {
                    if (returnToOverview) this.returnToOverview(false);
                });
        }

        if (!this.ecmrConsignmentFormGroup.valid) {
            this.toastr.error(
                this.translateService.instant('ECMR.ecmr_editor.invalid_form')
            );
        }
    }

    disableSave() {
        if (this.isReadOnly) {
            return true;
        }

        if (
            this.userEcmrRoles.includes(EcmrRole.Reader) &&
            this.userEcmrRoles.length == 1
        ) {
            return true;
        }
        if (
            this.userEcmrRoles.includes(EcmrRole.Sender) &&
            this.canFillSenderFields
        ) {
            return false;
        }
        if (
            this.userEcmrRoles.includes(EcmrRole.Carrier) &&
            (this.canFillCarrierFields || this.canCarrierEditInTransport)
        ) {
            return false;
        }
        return !(
            this.userEcmrRoles.includes(EcmrRole.Consignee) &&
            this.canFillConsigneeFields
        );
    }

    private resetClearedControls(group: FormGroup): void {
        for (const controlName in group.controls) {
            const control = group.controls[controlName];
            if (control instanceof FormGroup) {
                // recursive call for subgroups
                this.resetClearedControls(control);
            } else if (control.value === '') {
                control.reset();
            }
        }
    }

    private _filter(value: string): string[] {
        if (value) {
            const filteredValue: string = value.toUpperCase();
            return this.countries.filter(option =>
                option.includes(filteredValue)
            );
        } else {
            return this.countries;
        }
    }

    async saveEcmr(returnToOverview: boolean) {
        this.resetClearedControls(this.ecmrConsignmentFormGroup);

        this.ecmrConsignmentFormGroup.reset(
            this.ecmrConsignmentFormGroup.getRawValue()
        );
        if (
            this.ecmrConsignmentFormGroup.valid &&
            (this.editorMode == EditorMode.ECMR_NEW ||
                this.editorMode == EditorMode.ECMR_COPY)
        ) {
            const formValue: EcmrConsignment = this.transformItemsForApi(
                this.ecmrConsignmentFormGroup.getRawValue()
            );

            const ecmr: Ecmr = {
                ecmrId: null,
                ecmrConsignment: formValue,
            };

            try {
                const ecmrResult: Ecmr = await lastValueFrom(
                    this.ecmrEditorService.saveEcmr(
                        ecmr,
                        this.sessionService.currentOrganizationSiteKey()
                    )
                );

                if (ecmrResult) {
                    this.toastr.success(
                        this.translateService.instant(
                            'ECMR.ecmr_editor.save_success'
                        )
                    );
                    if (returnToOverview) {
                        this.returnToOverview(false);
                    } else {
                        this.ecmrToEdit = ecmrResult;
                        this.id = ecmrResult.ecmrId!;
                        this.editorMode = EditorMode.ECMR_EDIT;
                    }
                }
            } catch (error) {
                this.toastr.error(
                    this.translateService.instant(
                        'ECMR.ecmr_editor.save_failure'
                    )
                );
            }
        } else if (
            this.ecmrConsignmentFormGroup.valid &&
            this.editorMode == EditorMode.ECMR_EDIT
        ) {
            this.ecmrToEdit.ecmrConsignment = this.transformItemsForApi(
                this.ecmrConsignmentFormGroup.getRawValue()
            );

            try {
                let ecmrResult: Ecmr;
                if (this.isExternalUser) {
                    ecmrResult = await lastValueFrom(
                        this.externalUserService.updateEcmr(
                            this.ecmrToEdit,
                            this.userToken,
                            this.tan
                        )
                    );
                } else {
                    ecmrResult = await lastValueFrom(
                        this.ecmrEditorService.updateEcmr(
                            this.ecmrToEdit,
                            this.sessionService.currentOrganizationSiteKey()
                        )
                    );
                }

                if (ecmrResult) {
                    this.loadEcmr(ecmrResult);
                    this.toastr.success(
                        this.translateService.instant(
                            'ECMR.ecmr_editor.save_success'
                        )
                    );
                    if (returnToOverview) this.returnToOverview(false);
                } else {
                    this.toastr.error(
                        this.translateService.instant(
                            'ECMR.ecmr_editor.save_failure'
                        )
                    );
                }
            } catch (error) {
                this.toastr.error(
                    this.translateService.instant(
                        'ECMR.ecmr_editor.save_failure'
                    )
                );
            }
        } else {
            this.ecmrConsignmentFormGroup.markAllAsTouched();
        }
    }

    private loadEcmr(data: Ecmr) {
        this.ecmrToEdit = data;
        this.ecmrConsignment = data.ecmrConsignment;
        this.ecmrConsignmentFormGroup.controls.itemList.controls = [];
        this.ecmrConsignment.itemList.forEach(() => {
            this.addNewItem();
        });
        this.ecmrConsignmentFormGroup.patchValue(this.ecmrConsignment as any);
        this.loadBarcodesIntoForm();
        this.loadListsIntoForm();

        this.consigneeSignature =
            this.ecmrConsignment.goodsReceived.consigneeSignature;
        this.loadSealMetadata();
    }

    transformToUppercase(forControl: string) {
        let formControl: FormControl<string | null>;

        if (forControl === 'sender') {
            formControl =
                this.ecmrConsignmentFormGroup.controls.senderInformation
                    .controls.senderCountryCode.controls.value;
        } else if (forControl === 'carrier') {
            formControl =
                this.ecmrConsignmentFormGroup.controls.carrierInformation
                    .controls.carrierCountryCode.controls.value;
        } else if (forControl === 'successiveCarrier') {
            formControl =
                this.ecmrConsignmentFormGroup.controls
                    .successiveCarrierInformation.controls
                    .successiveCarrierCountryCode.controls.value;
        } else if (forControl === 'consignee') {
            formControl =
                this.ecmrConsignmentFormGroup.controls.consigneeInformation
                    .controls.consigneeCountryCode.controls.value;
        }

        const currentValue = formControl!.value!;

        if (currentValue) {
            formControl!.setValue(currentValue.toUpperCase(), {
                emitEvent: false,
            });
        }
    }

    checkCountry(forControl: string) {
        let formControl: FormControl<string | null>;
        if (forControl === 'sender') {
            formControl =
                this.ecmrConsignmentFormGroup.controls.senderInformation
                    .controls.senderCountryCode.controls.value;
        } else if (forControl === 'carrier') {
            formControl =
                this.ecmrConsignmentFormGroup.controls.carrierInformation
                    .controls.carrierCountryCode.controls.value;
        } else if (forControl === 'successiveCarrier') {
            formControl =
                this.ecmrConsignmentFormGroup.controls
                    .successiveCarrierInformation.controls
                    .successiveCarrierCountryCode.controls.value;
        } else if (forControl === 'consignee') {
            formControl =
                this.ecmrConsignmentFormGroup.controls.consigneeInformation
                    .controls.consigneeCountryCode.controls.value;
        }

        const countryIsFromList = this.countries.find(country =>
            country.includes(formControl.value!)
        );
        if (!countryIsFromList && formControl!.value) {
            formControl!.setErrors({ invalidCountryCode: true });
        }
    }

    checkControls(
        group: FormGroup,
        excludedElements: string[]
    ): AbstractControl[] {
        const invalidFields: AbstractControl[] = [];
        for (const controlName in group.controls) {
            const control = group.controls[controlName];
            if (control instanceof FormGroup) {
                const subResult = this.checkControls(control, excludedElements);
                if (subResult.length > 0) {
                    invalidFields.push(...subResult);
                }
            } else if (control instanceof FormArray) {
                if (!excludedElements.includes(controlName)) {
                    const hasValidEntry = control.controls.some(
                        (c: AbstractControl) => c.value && c.value.trim() !== ''
                    );
                    if (!hasValidEntry && control.controls.length > 0) {
                        // Mark the first child control so the template's
                        // per-control mandatoryForSigning mat-error is shown,
                        // like every other required field.
                        invalidFields.push(control.controls[0]);
                    }
                }
            } else if (
                !excludedElements.includes(controlName) &&
                (control.value === null || control.value === '')
            ) {
                invalidFields.push(control);
            }
        }
        return invalidFields;
    }

    /**
     * DeCA (Spanish transport paper) trigger: true when any supply-chain actor
     * (sender, carrier, consignee or successive carrier) is located in Spain.
     * Uses getRawValue() because the country inputs are dynamically disabled and
     * a disabled control's .value is undefined.
     */
    private hasSpanishActor(): boolean {
        const c = this.ecmrConsignmentFormGroup.controls;
        const countryValues = [
            c.senderInformation.controls.senderCountryCode.controls.value,
            c.carrierInformation.controls.carrierCountryCode.controls.value,
            c.consigneeInformation.controls.consigneeCountryCode.controls.value,
            c.successiveCarrierInformation.controls.successiveCarrierCountryCode
                .controls.value,
        ];
        return countryValues.some(
            ctrl => ctrl.getRawValue() === CountryCode.ES
        );
    }

    /**
     * Clears the synthetic DeCA `mandatoryForSigning` error from the tax-number
     * controls. Uses updateValueAndValidity() rather than setErrors(null) so the
     * control's own validators (maxLength) are re-run and any legitimate error is
     * preserved, while the cross-field Spanish requirement is dropped.
     */
    private clearTaxMandatoryForSigning(): void {
        const c = this.ecmrConsignmentFormGroup.controls;
        [
            c.senderInformation.controls.taxNumber,
            c.carrierInformation.controls.taxNumber,
            c.successiveCarrierInformation.controls.taxNumber,
        ].forEach(ctrl => {
            if (ctrl.hasError('mandatoryForSigning')) {
                ctrl.updateValueAndValidity();
            }
        });
    }

    /**
     * True when any successive-carrier input holds a value (incl. contact data,
     * tax number and non-blank vehicle-configuration rows). Uses getRawValue()
     * because the inputs are dynamically disabled.
     */
    private hasSuccessiveCarrierInput(): boolean {
        const hasValue = (control: AbstractControl): boolean => {
            if (control instanceof FormGroup || control instanceof FormArray) {
                return Object.values(control.controls).some(hasValue);
            }
            const value = control.getRawValue();
            return typeof value === 'string'
                ? value.trim() !== ''
                : value !== null && value !== undefined;
        };
        return hasValue(
            this.ecmrConsignmentFormGroup.controls.successiveCarrierInformation
        );
    }

    /**
     * Clears the conditional successive-carrier `mandatoryForSigning` errors.
     * Like clearTaxMandatoryForSigning(), updateValueAndValidity() re-runs the
     * controls' own validators instead of dropping all errors.
     */
    private clearSuccessiveCarrierMandatoryForSigning(): void {
        const clear = (control: AbstractControl): void => {
            if (control instanceof FormGroup || control instanceof FormArray) {
                Object.values(control.controls).forEach(clear);
            } else if (control.hasError('mandatoryForSigning')) {
                control.updateValueAndValidity();
            }
        };
        clear(
            this.ecmrConsignmentFormGroup.controls.successiveCarrierInformation
        );
    }

    senderFieldsAreValid(): boolean {
        const invalidFields: AbstractControl[] = [];
        const invalidVolumes: AbstractControl[] = [];
        invalidFields.push(
            ...this.checkControls(
                this.ecmrConsignmentFormGroup.controls.senderInformation,
                ['senderPersonName', 'region', 'email', 'phone', 'taxNumber']
            )
        );
        if (
            this.ecmrConsignmentFormGroup.controls.multiConsigneeShipment?.controls.isMultiConsigneeShipment?.getRawValue() !==
            true
        ) {
            invalidFields.push(
                ...this.checkControls(
                    this.ecmrConsignmentFormGroup.controls.consigneeInformation,
                    ['consigneePersonName', 'region', 'email', 'phone']
                )
            );
        }
        invalidFields.push(
            ...this.checkControls(
                this.ecmrConsignmentFormGroup.controls.takingOverTheGoods,
                ['region']
            )
        );
        invalidFields.push(
            ...this.checkControls(
                this.ecmrConsignmentFormGroup.controls.carrierInformation,
                [
                    'carrierDriverName',
                    'region',
                    'email',
                    'carrierPhone',
                    'driverPhone',
                    'taxNumber',
                    // Vehicle configuration is carrier-owned and only required at
                    // carrier-seal time; exclude from the sender-seal check.
                    // (checkControls' FormArray branch also assumes string-valued
                    // controls and would throw on this FormArray-of-FormGroups.)
                    'vehicleConfigurationHistory',
                ]
            )
        );
        // The successive carrier is optional, but once any of its fields is
        // filled it needs the same mandatory fields as the carrier. Clear a
        // previous attempt's requirement first so it lifts once the section is
        // emptied again.
        this.clearSuccessiveCarrierMandatoryForSigning();
        const successiveCarrierInUse = this.hasSuccessiveCarrierInput();
        if (successiveCarrierInUse) {
            invalidFields.push(
                ...this.checkControls(
                    this.ecmrConsignmentFormGroup.controls
                        .successiveCarrierInformation,
                    [
                        'successiveCarrierDriverName',
                        'region',
                        'email',
                        'carrierPhone',
                        'driverPhone',
                        'taxNumber',
                        // Optional for the successive carrier (and a
                        // FormArray-of-FormGroups checkControls can't handle).
                        'vehicleConfigurationHistory',
                    ]
                )
            );
        }
        if (
            this.ecmrConsignmentFormGroup.controls.itemList.controls.length > 0
        ) {
            for (const itemGroup of this.itemList.controls) {
                const volumeControl =
                    itemGroup.controls.volumeInM3.controls
                        .supplyChainConsignmentItemGrossVolume;
                if (volumeControl.value == 0) {
                    volumeControl.setErrors({ minValue: true });
                    invalidVolumes.push(volumeControl);
                }

                invalidFields.push(...this.checkControls(itemGroup, []));
            }
        }

        invalidFields.push(
            ...this.checkControls(
                this.ecmrConsignmentFormGroup.controls.established,
                []
            )
        );

        // DeCA (Spain): when any supply-chain actor is located in Spain, the tax
        // numbers of consignor, carrier and (if in use) successive carrier are
        // mandatory for signing. taxNumber stays excluded from checkControls
        // above (same leaf name for all actors), so flag it explicitly here.
        // Clear any previously-applied requirement first so it lifts once no
        // actor is in Spain; then re-apply below if Spain still applies (keeps
        // this check idempotent across repeated sign attempts).
        this.clearTaxMandatoryForSigning();
        if (this.hasSpanishActor()) {
            const c = this.ecmrConsignmentFormGroup.controls;
            const isEmpty = (ctrl: AbstractControl) =>
                ctrl.getRawValue() === null || ctrl.getRawValue() === '';

            const senderTax = c.senderInformation.controls.taxNumber;
            const carrierTax = c.carrierInformation.controls.taxNumber;
            if (isEmpty(senderTax)) invalidFields.push(senderTax);
            if (isEmpty(carrierTax)) invalidFields.push(carrierTax);

            const succTax = c.successiveCarrierInformation.controls.taxNumber;
            if (successiveCarrierInUse && isEmpty(succTax)) {
                invalidFields.push(succTax);
            }
        }

        for (const control of invalidFields) {
            control.setErrors({ mandatoryForSigning: true });
        }
        return invalidFields.length == 0 && invalidVolumes.length == 0;
    }

    get senderSealMetadata(): SealMetadata | undefined {
        return this.sealMetadata.find(s => s.role === 'SENDER');
    }

    /** DeCA note shows only once the sender has signed and the tax numbers make it conform. */
    get dcaCompliant(): boolean {
        return (
            !!this.senderSealMetadata &&
            !!this.ecmrConsignment &&
            isEcmrDcaCompliant(this.ecmrConsignment)
        );
    }

    get carrierSealMetadata(): SealMetadata | undefined {
        return this.sealMetadata.find(s => s.role === 'CARRIER');
    }

    get consigneeSealMetadata(): SealMetadata | undefined {
        return this.sealMetadata.find(s => s.role === 'CONSIGNEE');
    }

    private loadSealMetadata() {
        if (!this.id) return;
        const obs = this.isExternalUser
            ? this.externalUserService.getSealMetadata(
                  this.id,
                  this.userToken,
                  this.tan
              )
            : this.ecmrEditorService.getSealMetadata(this.id);
        obs.subscribe(metadata => {
            this.sealMetadata = metadata;
            this.cd.detectChanges();
        });
    }

    sealEcmr(): Observable<Ecmr> {
        const dialogRef = this.matDialog.open(EcmrConfirmationDialogComponent, {
            data: {
                text: 'ECMR.ecmr_editor.eseal_confirmation',
            },
        });

        return dialogRef.afterClosed().pipe(
            filter((result: ConfirmationDialogResult) => result.isConfirmed),
            switchMap(() => {
                this.ecmrToEdit.ecmrConsignment = this.transformItemsForApi(
                    this.ecmrConsignmentFormGroup.getRawValue()
                );
                return this.isExternalUser
                    ? this.externalUserService.updateEcmr(
                          this.ecmrToEdit,
                          this.userToken,
                          this.tan
                      )
                    : this.ecmrEditorService.updateEcmr(
                          this.ecmrToEdit,
                          this.sessionService.currentOrganizationSiteKey()
                      );
            }),
            switchMap(() =>
                this.isExternalUser
                    ? this.externalUserService.sealEcmr(
                          this.id,
                          this.userToken,
                          this.tan
                      )
                    : this.ecmrEditorService.sealEcmr(this.id)
            ),
            switchMap(() =>
                this.isExternalUser
                    ? this.externalUserService.getEcmrWithTan(
                          this.id,
                          this.userToken,
                          this.tan
                      )
                    : this.ecmrEditorService.getEcmr(this.id)
            )
        );
    }

    async sealSender() {
        this.resetClearedControls(this.ecmrConsignmentFormGroup);
        if (
            this.senderFieldsAreValid() &&
            this.ecmrConsignmentFormGroup.valid
        ) {
            if (!this.ecmrToEdit) {
                await this.saveEcmr(false);
            }

            this.sealEcmr().subscribe({
                next: ecmr => {
                    this.loadEcmr(ecmr);
                    this.setFormConstraints();
                    this.cd.detectChanges();
                },
            });
        } else {
            this.ecmrConsignmentFormGroup.markAllAsTouched();
            this.toastr.warning(
                this.translateService.instant(
                    'ECMR.ecmr_editor.invalid_form_for_signature'
                )
            );
        }
    }

    sealCarrier() {
        this.resetClearedControls(this.ecmrConsignmentFormGroup);
        if (!this.carrierFieldsAreValid()) {
            this.ecmrConsignmentFormGroup.markAllAsTouched();
            this.toastr.warning(
                this.translateService.instant(
                    'ECMR.ecmr_editor.invalid_form_for_signature'
                )
            );
            return;
        }
        this.sealEcmr().subscribe(ecmr => {
            this.loadEcmr(ecmr);
            this.setFormConstraints();
            this.cd.detectChanges();
        });
    }

    /**
     * Sealing as carrier requires at least one vehicle configuration with a
     * motor license plate and a valid past timestamp (backend LOADING -> seal
     * rejects an empty vehicleConfigurationHistory). If there is none, the
     * blank date / motor plate of the first row are flagged mandatoryForSigning.
     */
    carrierFieldsAreValid(): boolean {
        this.ensureCarrierVehicleConfig();
        const configs = this.vehicleConfigArray('carrier');
        const requiredControls = (group: VehicleConfigurationFormGroup) => [
            group.controls.timestamp,
            group.controls.motorVehicleLicensePlate,
        ];

        // Drop the requirement from a previous sign attempt first, so e.g.
        // filling only a second row does not leave the first one invalid.
        // updateValueAndValidity() re-runs the regular validators.
        configs.controls.forEach(group =>
            requiredControls(group).forEach(ctrl => {
                if (ctrl.hasError('mandatoryForSigning')) {
                    ctrl.updateValueAndValidity();
                }
            })
        );

        const isBlank = (ctrl: AbstractControl) =>
            ctrl.value === null ||
            (typeof ctrl.value === 'string' && ctrl.value.trim() === '');
        const hasCompleteConfig = configs.controls.some(group =>
            requiredControls(group).every(ctrl => !isBlank(ctrl))
        );
        if (!hasCompleteConfig) {
            requiredControls(configs.at(0))
                .filter(isBlank)
                .forEach(ctrl =>
                    ctrl.setErrors({
                        ...ctrl.errors,
                        mandatoryForSigning: true,
                    })
                );
            return false;
        }
        return !configs.invalid;
    }

    deleteItem(i: number) {
        this.ecmrConsignmentFormGroup.controls.itemList.controls.splice(i, 1);
        this.ecmrConsignmentFormGroup.controls.itemList.updateValueAndValidity();
    }

    signConsignee() {
        this.resetClearedControls(this.ecmrConsignmentFormGroup);
        if (
            this.consigneeFieldsAreValid() &&
            this.ecmrConsignmentFormGroup.valid
        ) {
            this.matDialog
                .open(SignaturePadDialogComponent, {
                    width: '80vw',
                    height: '80vh',
                })
                .afterClosed()
                .pipe(
                    filter(result => result),
                    switchMap(signatureData => {
                        this.ecmrToEdit.ecmrConsignment =
                            this.transformItemsForApi(
                                this.ecmrConsignmentFormGroup.getRawValue()
                            );
                        const consigneeInfo =
                            this.ecmrToEdit.ecmrConsignment
                                .consigneeInformation;
                        this.ecmrToEdit.ecmrConsignment.goodsReceived.consigneeSignature =
                            {
                                type: SignatureType.SignOnGlass,
                                data: signatureData,
                                timestamp: new Date(),
                                userName: this.isExternalUser
                                    ? (consigneeInfo.consigneePersonName ?? '')
                                    : this.sessionService.getSession().fullName,
                                userCompany:
                                    consigneeInfo.consigneeCompanyName ?? '',
                                userStreet: consigneeInfo.consigneeStreet ?? '',
                                userPostCode:
                                    consigneeInfo.consigneePostcode ?? '',
                                userCity: consigneeInfo.consigneeCity ?? '',
                                userCountry:
                                    consigneeInfo.consigneeCountryCode?.value ??
                                    '',
                            } as Signature;
                        return this.isExternalUser
                            ? this.externalUserService.updateEcmr(
                                  this.ecmrToEdit,
                                  this.userToken,
                                  this.tan
                              )
                            : this.ecmrEditorService.updateEcmr(
                                  this.ecmrToEdit,
                                  this.sessionService.currentOrganizationSiteKey()
                              );
                    }),
                    switchMap(() =>
                        this.isExternalUser
                            ? this.externalUserService.sealEcmr(
                                  this.id,
                                  this.userToken,
                                  this.tan
                              )
                            : this.ecmrEditorService.sealEcmr(this.id)
                    ),
                    switchMap(() =>
                        this.isExternalUser
                            ? this.externalUserService.getEcmrWithTan(
                                  this.id,
                                  this.userToken,
                                  this.tan
                              )
                            : this.ecmrEditorService.getEcmr(this.id)
                    )
                )
                .subscribe(ecmr => {
                    this.loadEcmr(ecmr);
                    this.setFormConstraints();
                    this.cd.detectChanges();
                });
        } else {
            this.ecmrConsignmentFormGroup.markAllAsTouched();
        }
    }

    consigneeFieldsAreValid(): boolean {
        const invalidFields: AbstractControl[] = this.checkControls(
            this.ecmrConsignmentFormGroup.controls.goodsReceived,
            [
                'consigneeReservationsObservations',
                'consigneeTimeOfArrival',
                'consigneeTimeOfDeparture',
                'consigneeSignature',
            ]
        );
        for (const control of invalidFields) {
            control.setErrors({ mandatoryForSigning: true });
        }
        return invalidFields.length == 0;
    }

    scrollToElement() {
        const element = document.getElementById('ecmr-signature-scroll');
        element?.scrollIntoView({ behavior: 'smooth' });
    }

    setFormConstraints() {
        this.canFillSenderFields = false;
        this.canFillCarrierFields = false;
        this.canFillConsigneeFields = false;
        this.canCarrierEditInTransport = false;
        if (this.userEcmrRoles.includes(EcmrRole.Sender)) {
            if (
                (this.ecmrToEdit != null &&
                    this.ecmrToEdit.ecmrStatus === EcmrStatus.NEW) ||
                !this.ecmrToEdit
            ) {
                this.canFillSenderFields = true;
            }
            if (
                this.ecmrToEdit != null &&
                this.editorMode === EditorMode.TEMPLATE_EDIT
            ) {
                this.canFillSenderFields = true;
            }
        }
        if (
            this.userEcmrRoles.includes(EcmrRole.Carrier) &&
            this.ecmrToEdit &&
            this.ecmrToEdit.ecmrStatus == EcmrStatus.LOADING
        ) {
            this.canFillCarrierFields = true;
        }

        // The carrier may amend vehicle configuration and goods-received while
        // the shipment is IN_TRANSPORT (backend update-authorization allows it).
        // The full carrier address block stays locked (canFillCarrierFields).
        if (
            this.userEcmrRoles.includes(EcmrRole.Carrier) &&
            this.ecmrToEdit &&
            this.ecmrToEdit.ecmrStatus == EcmrStatus.IN_TRANSPORT
        ) {
            this.canCarrierEditInTransport = true;
        }

        if (
            this.userEcmrRoles.includes(EcmrRole.Consignee) &&
            this.ecmrToEdit &&
            this.ecmrToEdit.ecmrStatus == EcmrStatus.IN_TRANSPORT
        ) {
            this.canFillConsigneeFields = true;

            if (
                !this.ecmrToEdit.ecmrConsignment.goodsReceived
                    .confirmedLogisticsLocationName
            ) {
                const consigneeDefaultLocation =
                    this.ecmrToEdit.ecmrConsignment.consigneeInformation
                        .consigneeCity;
                this.ecmrConsignmentFormGroup.controls.goodsReceived.controls.confirmedLogisticsLocationName.setValue(
                    consigneeDefaultLocation
                );
            }
            this.ecmrConsignmentFormGroup.controls.goodsReceived.controls.consigneeSignatureDate.setValue(
                new Date()
            );
        }
    }

    checkSignStatus() {
        return (
            this.canFillSenderFields ||
            this.canFillCarrierFields ||
            this.canFillConsigneeFields
        );
    }

    isTemplate(): boolean {
        return (
            this.editorMode == EditorMode.TEMPLATE_NEW ||
            this.editorMode == EditorMode.TEMPLATE_EDIT
        );
    }

    isNotEdit(): boolean {
        return !(
            this.editorMode === EditorMode.ECMR_EDIT ||
            this.editorMode === EditorMode.TEMPLATE_EDIT
        );
    }

    public getTransportType(ecmr: Ecmr): EcmrTransportType | null {
        return this.ecmrService.getTransportType(ecmr);
    }

    protected readonly EcmrTransportType = EcmrTransportType;

    protected readonly EcmrStatus = EcmrStatus;

    createBarcodeControl(
        value: string | null = null
    ): FormControl<string | null> {
        return new FormControl<string | null>(value, [
            Validators.minLength(2),
            Validators.maxLength(35),
        ]);
    }

    addBarcodeToItem(itemIndex: number): void {
        const barcodeArray =
            this.ecmrConsignmentFormGroup.controls.itemList.controls[itemIndex]
                ?.controls.marksAndNos.controls
                .logisticsShippingMarksCustomBarcodeList;
        barcodeArray?.push(this.createBarcodeControl());
    }

    removeBarcodeFromItem(itemIndex: number, barcodeIndex: number): void {
        const barcodeArray =
            this.ecmrConsignmentFormGroup.controls.itemList.controls[itemIndex]
                ?.controls.marksAndNos.controls
                .logisticsShippingMarksCustomBarcodeList;
        if (barcodeArray && barcodeArray.length > 1) {
            barcodeArray.removeAt(barcodeIndex);
        }
    }

    createVehicleConfigGroup(
        config: Partial<{
            timestamp: Date | null;
            motorVehicleLicensePlate: string | null;
            trailerLicensePlate: string | null;
            secondTrailerLicensePlate: string | null;
        }> = {}
    ): VehicleConfigurationFormGroup {
        // Date and motor plate are only required once the row is not blank
        // (vehicleConfigCompletenessValidator), so the always-present empty
        // carrier row does not invalidate the form for saving.
        return new FormGroup(
            {
                timestamp: new FormControl<Date | null>(
                    config.timestamp ?? null,
                    [pastDateValidator()]
                ),
                motorVehicleLicensePlate: new FormControl<string | null>(
                    config.motorVehicleLicensePlate ?? null,
                    [Validators.maxLength(255)]
                ),
                trailerLicensePlate: new FormControl<string | null>(
                    config.trailerLicensePlate ?? null,
                    [Validators.maxLength(255)]
                ),
                secondTrailerLicensePlate: new FormControl<string | null>(
                    config.secondTrailerLicensePlate ?? null,
                    [Validators.maxLength(255)]
                ),
            },
            { validators: vehicleConfigCompletenessValidator() }
        );
    }

    private vehicleConfigArray(
        target: 'carrier' | 'successiveCarrier'
    ): FormArray<VehicleConfigurationFormGroup> {
        return target === 'carrier'
            ? this.ecmrConsignmentFormGroup.controls.carrierInformation.controls
                  .vehicleConfigurationHistory
            : this.ecmrConsignmentFormGroup.controls
                  .successiveCarrierInformation.controls
                  .vehicleConfigurationHistory;
    }

    addVehicleConfig(target: 'carrier' | 'successiveCarrier'): void {
        this.vehicleConfigArray(target).push(this.createVehicleConfigGroup());
    }

    /**
     * The carrier always gets at least one (possibly empty) vehicle
     * configuration row, so that its mandatory fields can be highlighted when
     * the carrier tries to sign without filling them in.
     */
    private ensureCarrierVehicleConfig(): void {
        if (this.vehicleConfigArray('carrier').length === 0) {
            this.addVehicleConfig('carrier');
        }
    }

    removeVehicleConfig(
        target: 'carrier' | 'successiveCarrier',
        index: number
    ): void {
        this.vehicleConfigArray(target).removeAt(index);
    }

    createSpecialPermitGroup(
        permit: Partial<{
            type: string | null;
            reference: string | null;
            description: string | null;
        }> = {}
    ): SpecialPermitFormGroup {
        return new FormGroup({
            type: new FormControl<string | null>(permit.type ?? null, [
                Validators.maxLength(64),
            ]),
            reference: new FormControl<string | null>(
                permit.reference ?? null,
                [Validators.maxLength(64)]
            ),
            description: new FormControl<string | null>(
                permit.description ?? null,
                [Validators.maxLength(512)]
            ),
        });
    }

    addSpecialPermit(): void {
        this.ecmrConsignmentFormGroup.controls.specialPermits.push(
            this.createSpecialPermitGroup()
        );
    }

    removeSpecialPermit(index: number): void {
        this.ecmrConsignmentFormGroup.controls.specialPermits.removeAt(index);
    }

    private loadListsIntoForm() {
        const carrierArray = this.vehicleConfigArray('carrier');
        carrierArray.clear();
        this.ecmrConsignment.carrierInformation.vehicleConfigurationHistory?.forEach(
            config =>
                carrierArray.push(this.createVehicleConfigGroup(config as any))
        );
        this.ensureCarrierVehicleConfig();

        const successiveArray = this.vehicleConfigArray('successiveCarrier');
        successiveArray.clear();
        this.ecmrConsignment.successiveCarrierInformation.vehicleConfigurationHistory?.forEach(
            config =>
                successiveArray.push(
                    this.createVehicleConfigGroup(config as any)
                )
        );

        const permitArray =
            this.ecmrConsignmentFormGroup.controls.specialPermits;
        permitArray.clear();
        this.ecmrConsignment.specialPermits?.forEach(permit =>
            permitArray.push(this.createSpecialPermitGroup(permit))
        );
    }

    private loadBarcodesIntoForm() {
        this.ecmrConsignment.itemList.forEach((item, index) => {
            const barcodeList =
                item.marksAndNos.logisticsShippingMarksCustomBarcodeList;
            const barcodeArray =
                this.ecmrConsignmentFormGroup.controls.itemList.controls[index]
                    ?.controls.marksAndNos.controls
                    .logisticsShippingMarksCustomBarcodeList;
            if (barcodeArray) {
                barcodeArray.clear();
                if (barcodeList?.length) {
                    barcodeList.forEach(b =>
                        barcodeArray.push(this.createBarcodeControl(b.barcode))
                    );
                } else {
                    barcodeArray.push(this.createBarcodeControl());
                }
            }
        });
    }

    private transformItemsForApi(formValue: any): EcmrConsignment {
        formValue.itemList = formValue.itemList.map((item: any) => ({
            ...item,
            marksAndNos: {
                logisticsShippingMarksMarking:
                    item.marksAndNos.logisticsShippingMarksMarking,
                logisticsShippingMarksCustomBarcodeList:
                    item.marksAndNos.logisticsShippingMarksCustomBarcodeList
                        ?.filter((v: string | null) => v && v.trim() !== '')
                        .map((v: string) => ({ barcode: v })) || null,
            },
        }));

        // Drop fully-empty vehicle-config rows (no motor plate and no timestamp)
        // so we never serialize an all-null entry the backend would reject.
        const cleanVehicleConfigs = (configs: any[] | null | undefined) =>
            (configs ?? []).filter(
                (c: any) => c && (c.motorVehicleLicensePlate || c.timestamp)
            );
        if (formValue.carrierInformation) {
            formValue.carrierInformation.vehicleConfigurationHistory =
                cleanVehicleConfigs(
                    formValue.carrierInformation.vehicleConfigurationHistory
                );
        }
        if (formValue.successiveCarrierInformation) {
            formValue.successiveCarrierInformation.vehicleConfigurationHistory =
                cleanVehicleConfigs(
                    formValue.successiveCarrierInformation
                        .vehicleConfigurationHistory
                );
        }

        // Drop fully-empty special-permit rows (all three fields blank).
        formValue.specialPermits = (formValue.specialPermits ?? []).filter(
            (p: any) => p && (p.type || p.reference || p.description)
        );

        return formValue as EcmrConsignment;
    }
}
